/**
 * Центр уведомлений в приложении (ПОРТ-60) поверх модуля notifications бэкенда:
 * GET /api/v1/notifications (курсор), GET /unread-count, POST /{id}/read, POST /read-all.
 * Сервер формирует заголовок и текст на языке X-Locale; X-Clinic-Id ограничивает список клиникой.
 * Запросы — через типизированный клиент (TD-27).
 */
import type { DodocClient } from '@dodoc/api-client';
import { call } from './http';

export type NotificationPriority = 'normal' | 'high' | 'critical';

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  /** Путь в интерфейсе, куда ведёт уведомление. */
  link: string | null;
  priority: NotificationPriority;
  clinicId: string | null;
  createdAt: string;
  readAt: string | null;
}

export interface NotificationPage {
  items: AppNotification[];
  nextCursor: string | null;
}

export function createNotificationsApi(api: DodocClient) {
  return {
    list: async (params: { cursor?: string | null; limit?: number; unreadOnly?: boolean }, signal?: AbortSignal): Promise<NotificationPage> =>
      (await call(
        api.GET('/api/v1/notifications', {
          params: { query: { cursor: params.cursor ?? undefined, limit: params.limit ?? 20, unreadOnly: params.unreadOnly || undefined } },
          signal,
        }),
      )) as NotificationPage,
    unreadCount: async (signal?: AbortSignal) => (await call(api.GET('/api/v1/notifications/unread-count', { signal }))).count,
    markRead: (id: string) => call(api.POST('/api/v1/notifications/{id}/read', { params: { path: { id } } })),
    markAllRead: () => call(api.POST('/api/v1/notifications/read-all', {})),
  };
}

export type NotificationsApi = ReturnType<typeof createNotificationsApi>;

/** Ссылку уведомления открываем только внутри приложения (защита от подмены адреса). */
export function safeInternalLink(link: string | null): string | null {
  if (!link || !link.startsWith('/') || link.startsWith('//')) return null;
  return link;
}
